import { useState, useEffect, useMemo } from 'react';
import { getPublicProducts, type PublicProductSummary } from '@/application/products/product-query-service';
import { exampleProducts } from '@/data/example-products';
import { ProductCard } from '@/components/inventory/ProductCard';
import { InventoryFilters } from '@/components/inventory/InventoryFilters';
import { EmptyInventory, SoldInspirationBanner } from '@/components/inventory/InventoryStates';

export function InventoryPage() {
  const [products, setProducts] = useState<PublicProductSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    setLoading(true);
    getPublicProducts()
      .then(setProducts)
      .catch(() => {
        // Fallback to example data when API is unavailable (local dev without backend)
        setProducts(exampleProducts);
        setError(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (selectedSize && !p.sizes.includes(selectedSize)) return false;
      if (selectedStatus && p.status !== selectedStatus) return false;
      return true;
    });
  }, [products, selectedSize, selectedStatus]);

  const availableSizes = useMemo(() => {
    const sizes = new Set(products.flatMap((p) => p.sizes));
    return [...sizes].sort();
  }, [products]);

  const hasSoldItems = products.some(
    (p) => p.status === 'sold' || p.status === 'reserved',
  );

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <p className="text-[var(--color-brand-muted)]">Loading collection...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <p className="text-red-600">Unable to load inventory. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <h1 className="text-3xl font-bold">Our Collection</h1>
        <InventoryFilters
          selectedSize={selectedSize}
          selectedStatus={selectedStatus}
          onSizeChange={setSelectedSize}
          onStatusChange={setSelectedStatus}
          availableSizes={availableSizes}
        />
      </div>

      {hasSoldItems && <SoldInspirationBanner />}

      {filteredProducts.length === 0 ? (
        <EmptyInventory />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} {...product} />
          ))}
        </div>
      )}
    </div>
  );
}
